import { HTTP } from "./http";
import { Documents } from "./documents-service";
import moment from "moment";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER =
    "http://www.localhost:8888/back-end-endpoint-performance-reviews.php";
} else {
  API_SERVER =
    "/personal-training-BE/back-end-endpoint-performance-reviews.php";
}

/**
 * Coach Performance Review API Service
 * Handles all API interactions for the Coach Performance Review feature
 */
const CoachPerformanceReviewService = {
  /**
   * Get all coach performance reports for a player
   * @param {number} playerId - The player's user ID
   * @param {string} seasonStartDate - Season start date (YYYY-MM-DD)
   * @param {string} seasonEndDate - Season end date (YYYY-MM-DD)
   * @returns {Promise} - API response with array of reports
   */
  async getReports(playerId, seasonStartDate, seasonEndDate) {
    const params = {
      action: "getPlayerCoachSignedPerformanceReport",
      id: playerId,
      season_start_date: seasonStartDate,
      season_end_date: seasonEndDate,
    };
    return HTTP.post(API_SERVER, params);
  },

  /**
   * Save a new coach performance report
   * @param {Object} reportData - The report data to save
   * @returns {Promise} - API response
   */
  async saveReport(reportData) {
    const params = {
      action: "savePlayerCoachSignedPerformanceReport",
      created_date: moment().format("YYYY-MM-DD"),
      ...reportData,
    };
    return HTTP.post(API_SERVER, params);
  },

  /**
   * Update an existing coach performance report
   * @param {Object} reportData - The report data to update
   * @returns {Promise} - API response
   */
  async updateReport(reportData) {
    const params = {
      action: "updatePlayerCoachSignedPerformanceReport",
      ...reportData,
    };
    return HTTP.post(API_SERVER, params);
  },

  /**
   * Delete a coach performance report
   * @param {number} reportId - The report ID to delete
   * @returns {Promise} - API response
   */
  async deleteReport(reportId) {
    const params = {
      action: "deletePlayerCoachSignedPerformanceReport",
      id: reportId,
    };
    return HTTP.post(API_SERVER, params);
  },

  /**
   * Publish a coach performance report
   * @param {Object} reportData - The report data with id
   * @returns {Promise} - API response
   */
  async publishReport(reportData) {
    const params = {
      action: "updatePlayerCoachSignedPerformanceReport",
      ...reportData,
      published: 1,
      status: "published",
    };
    return HTTP.post(API_SERVER, params);
  },

  /**
   * Unpublish a coach performance report
   * @param {Object} reportData - The report data with id
   * @returns {Promise} - API response
   */
  async unpublishReport(reportData) {
    const params = {
      action: "updatePlayerCoachSignedPerformanceReport",
      ...reportData,
      published: 0,
      status: "live",
    };
    return HTTP.post(API_SERVER, params);
  },

  /**
   * Get signatures for a coach performance report
   * @param {number} reportId - The report/document ID
   * @returns {Promise} - API response with signature data
   */
  async getSignatures(reportId) {
    const params = {
      id: reportId,
      action: "getPlayerCoachSignedPerformanceSignatures",
    };
    return Documents.find(params);
  },

  /**
   * Save a signature for a coach performance report
   * @param {string} signatureName - Name/type of signature (playerReportSignature, parentReportSignature, coachSignature)
   * @param {string} signatureData - Base64 signature image data
   * @param {number} documentId - The document/report ID
   * @returns {Promise} - API response
   */
  async saveSignature(signatureName, signatureData, documentId) {
    const params = {
      fileName: signatureName,
      imageFile: signatureData,
      document_id: documentId,
      action: "saveSignatureWithDocumentId",
      created_date: moment().format("YYYY-MM-DD"),
      type: signatureName,
    };
    return Documents.find(params);
  },

  /**
   * Get the default empty report data structure
   * @returns {Object} - Empty report data structure
   */
  getEmptyReportData() {
    return {
      id: undefined,
      created_date: null,
      user_id: "",
      coach_id: null,
      review_type: "text", // 'text' or 'traffic-light'
      technical_player_comments: "",
      technical_coach_comments: "",
      tactical_player_comments: "",
      tactical_coach_comments: "",
      physical_player_comments: "",
      physical_coach_comments: "",
      physcological_player_comments: "",
      physcological_coach_comments: "",
      player_comments: "",
      coach_comments: "",
      player_signature_id: null,
      parent_signature_id: null,
      coach_signature_id: null,
      published: 0,
      status: "Draft",
      // Traffic light data (JSON strings)
      traffic_light_player_data: null,
      traffic_light_coach_data: null,
      // Additional fields for display
      userImage: null,
      name: "",
      playerReportSignature: null,
      parentReportSignature: null,
      coachSignature: null,
    };
  },

  /**
   * Get the default signature state
   * @returns {Object} - Default signature state
   */
  getDefaultSignatureState() {
    return {
      player_signature_change: true,
      coach_signature_change: true,
      parent_signature_change: true,
    };
  },

  /**
   * Performance categories for the review
   */
  performanceCategories: ["Technical", "Tactical", "Physical", "Psychological"],

  /**
   * Review status options
   */
  statusOptions: [
    { value: "Draft", text: "Draft" },
    { value: "live", text: "Live" },
    // Published is set programmatically, not manually selected
  ],

  /**
   * Check if report can be published
   * @param {Object} reportData - The report data
   * @returns {boolean} - Whether the report can be published
   */
  canPublish(reportData) {
    return (
      reportData.status !== "published" &&
      reportData.player_signature_id !== null &&
      reportData.coach_signature_id !== null
    );
  },

  /**
   * Check if report is editable
   * @param {Object} reportData - The report data
   * @returns {boolean} - Whether the report can be edited
   */
  isEditable(reportData) {
    return reportData.status !== "published";
  },
};

export default CoachPerformanceReviewService;
export { CoachPerformanceReviewService };
